/**
 * Рабочее место согласования: слева бэклог, справа выбранный черновик (docs/ui-design.md §5.1).
 *
 * Бэклог загружается здесь, а не внутри списка, по одной причине: после любого решения
 * («утвердить», «переделать», «отложить») меняется и панель, и строка списка. Один
 * владелец данных — один refetch, и список не остаётся с прежним статусом.
 */

import { type JSX, useCallback } from 'react';
import { Link } from 'wouter';
import type { BacklogResponse } from '../api/types.ts';
import { useResource } from '../api/useResource.ts';
import { Shell } from '../components/Shell.tsx';
import { ShellRevWatcher } from '../components/shellSettings.ts';
import { BacklogAside } from './BacklogAside.tsx';
import { DraftPanel } from './DraftPanel.tsx';

export function WorkspacePage({
  brand,
  draftId,
  versionNo = null,
}: {
  brand: string;
  draftId: string | null;
  versionNo?: number | null;
}): JSX.Element {
  const backlog = useResource<BacklogResponse>(
    `/api/v1/backlog?brand=${encodeURIComponent(brand)}&limit=200`,
  );
  const { refetch } = backlog;
  const onChanged = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <Shell brand={brand} active="backlog">
      {/* Шапка сообщила «настройки изменились» (выключатель автоодобрения вернул посты на
          согласование) — бэклог перечитывается; экран черновика перечитывает себя сам. */}
      <ShellRevWatcher onRev={onChanged} />
      <BacklogAside
        brand={brand}
        items={backlog.data}
        loading={backlog.loading}
        error={backlog.error}
        selectedDraftId={draftId}
      />
      {draftId === null ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            padding: 32,
          }}
        >
          <div className="micro">Выберите пост слева.</div>
          {/* Пакетный режим — вход отсюда, как в прототипе дизайна: он про весь бэклог сразу,
              а не про открытый пост, поэтому кнопке место на пустом экране, а не в панели поста. */}
          <Link href={`/b/${brand}/batch`} className="btn btn-ghost btn-sm">
            Утвердить пакетом на неделю…
          </Link>
        </div>
      ) : (
        /*
         * `key` — ПОСТ, а не версия. Маршрутизатор отдаёт один и тот же элемент на всех адресах
         * рабочего места (`Switch` wouter клонирует найденный маршрут без ключа), и без ключа
         * переход к другому посту переиспользовал бы панель вместе с тем, что в ней ещё идёт:
         * загрузка PDF поста A доезжала до экрана поста B и перечитывала туда версию A (ревью
         * 19.09.2026, ui-1). С ключом прежняя панель размонтируется, и её хуки гасят своё сами:
         * загрузка файла и картинки обрывается, ответ «Перевести в расширенное» — отбрасывается.
         * Переход между версиями того же поста (`/v/:n`) панель не пересоздаёт — загрузка на
         * нём и должна доехать до новой версии.
         */
        <DraftPanel
          key={draftId}
          brand={brand}
          draftId={draftId}
          versionNo={versionNo}
          onChanged={onChanged}
          auto={backlog.data?.find((i) => i.draft_id === draftId)?.auto === true}
        />
      )}
    </Shell>
  );
}
